import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Toolbar,
  AppBar,
  Grid,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha,
  Fade,
  Snackbar,
  Alert,
  Tooltip,
  Popper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Flag as FlagIcon,
  LocalOffer as TagIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatListBulleted as BulletIcon,
  FormatListNumbered as NumberedIcon,
  Code as CodeIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  Assignment as AssignmentIcon,
  TableChart as TableIcon,
  HorizontalRule as HRuleIcon,
  FormatQuote as QuoteIcon,
  Title as HeaderIcon,
  ViewColumn as ColumnsIcon,
  CheckBox as ChecklistIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as DangerIcon,
  CheckCircle as SuccessIcon,
  List as TOCIcon,
  Visibility as PreviewIcon,
  VisibilityOff as EditIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { supabase } from '../../../supabaseClient';

const priorityColors = {
  low: '#4caf50',
  medium: '#ff9800',
  high: '#f44336',
  urgent: '#9c27b0'
};

// Profile Avatar Component
const ProfileAvatar = ({ userId, user, size = 28, sx = {} }) => {
  const [avatarUrl, setAvatarUrl] = useState('');
  
  useEffect(() => {
    if (!userId) return;
    
    const loadAvatar = async () => {
      try {
        const { data: files, error } = await supabase.storage
          .from('profile-avatars')
          .list(`${userId}/`, {
            limit: 1,
            sortBy: { column: 'created_at', order: 'desc' }
          });

        if (error || !files || files.length === 0) return;

        const fileName = `${userId}/${files[0].name}`;
        const { data, error: signedUrlError } = await supabase.storage
          .from('profile-avatars')
          .createSignedUrl(fileName, 3600);
        
        if (!signedUrlError && data?.signedUrl) {
          setAvatarUrl(data.signedUrl);
        }
      } catch (err) {
        // Silent fail
      }
    };

    loadAvatar();
  }, [userId]);

  const getInitials = () => {
    if (user?.full_name) {
      return user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return '?';
  };

  return (
    <Avatar
      src={avatarUrl}
      sx={{ 
        width: size, 
        height: size, 
        fontSize: `${size * 0.4}px`,
        backgroundColor: 'grey.400',
        color: 'white',
        fontWeight: 500,
        ...sx
      }}
    >
      {getInitials()}
    </Avatar>
  );
};

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.grey[50], 0.5)})`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`,
  }
}));

const TodoImage = ({ src, alt, currentUser, ...props }) => {
  const theme = useTheme();
  const [signedUrl, setSignedUrl] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const getSignedUrl = React.useCallback(async (path) => {
    try {
      setIsLoading(true);
      
      // Try signed URL first
      const { data, error } = await supabase.storage
        .from('to-do-files')
        .createSignedUrl(path, 60 * 60);
      
      if (error) {
        console.error('Signed URL error:', error);
        
        // Fallback: try downloading the file directly
        const { data: downloadData, error: downloadError } = await supabase.storage
          .from('to-do-files')
          .download(path);
          
        if (downloadError) {
          console.error('Download error:', downloadError);
          throw downloadError;
        }
        
        // Create blob URL
        const url = URL.createObjectURL(downloadData);
        setSignedUrl(url);
        return;
      }
      
      setSignedUrl(data?.signedUrl || null);
    } catch (err) {
      console.error('Failed to create signed URL:', err);
      setSignedUrl(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (src && src.startsWith('https://todo-file/')) {
      const filePath = src.replace('https://todo-file/', '');
      getSignedUrl(filePath);
    } else {
      setSignedUrl(null);
    }
  }, [src, getSignedUrl]);

  const handleError = () => {
    if (src && src.startsWith('https://todo-file/')) {
      const filePath = src.replace('https://todo-file/', '');
      getSignedUrl(filePath);
    }
  };

  const effectiveSrc = src && src.startsWith('https://todo-file/') ? signedUrl : src;

  if (src && src.startsWith('https://todo-file/') && !effectiveSrc && isLoading) {
    return (
      <div style={{ 
        maxWidth: '100%', 
        height: '200px',
        borderRadius: '8px',
        marginTop: '8px',
        marginBottom: '8px',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed #ccc'
      }}>
        Loading image...
      </div>
    );
  }

  if (src && src.startsWith('https://todo-file/') && !effectiveSrc && !isLoading) {
    return (
      <div style={{ 
        maxWidth: '100%', 
        height: '200px',
        borderRadius: '8px',
        marginTop: '8px',
        marginBottom: '8px',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed #ccc',
        color: '#999'
      }}>
        Failed to load image
      </div>
    );
  }

  return (
    <img 
      {...props}
      src={effectiveSrc}
      alt={alt}
      onError={handleError}
      style={{ 
        maxWidth: '100%', 
        height: 'auto',
        borderRadius: '8px',
        marginTop: '8px',
        marginBottom: '8px'
      }} 
    />
  );
};

const MarkdownToolbar = ({ onInsert, onImageUpload, isUploading }) => {
  const theme = useTheme();
  const fileInputRef = React.useRef(null);

  const handleImageClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file && onImageUpload) {
      await onImageUpload(file);
    }
    event.target.value = '';
  };

  const tools = [
    { icon: <BoldIcon />, action: () => onInsert('**', '**'), tooltip: 'Bold (Ctrl+B)', shortcut: 'Ctrl+B' },
    { icon: <ItalicIcon />, action: () => onInsert('*', '*'), tooltip: 'Italic (Ctrl+I)', shortcut: 'Ctrl+I' },
    { icon: <BulletIcon />, action: () => onInsert('\n- ', ''), tooltip: 'Bullet List (Ctrl+Shift+8)', shortcut: 'Ctrl+Shift+8' },
    { icon: <NumberedIcon />, action: () => onInsert('\n1. ', ''), tooltip: 'Numbered List (Ctrl+Shift+7)', shortcut: 'Ctrl+Shift+7' },
    { icon: <CodeIcon />, action: () => onInsert('`', '`'), tooltip: 'Code (Ctrl+`)', shortcut: 'Ctrl+`' },
    { icon: <LinkIcon />, action: () => onInsert('[', '](url)'), tooltip: 'Link (Ctrl+K)', shortcut: 'Ctrl+K' },
    { icon: <TableIcon />, action: () => onInsert('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', ''), tooltip: 'Table (Ctrl+T)', shortcut: 'Ctrl+T' },
    { icon: <HRuleIcon />, action: () => onInsert('\n---\n', ''), tooltip: 'Horizontal Rule (Ctrl+H)', shortcut: 'Ctrl+H' },
    { icon: <ImageIcon />, action: handleImageClick, tooltip: isUploading ? 'Uploading...' : 'Upload Image (Ctrl+U)', shortcut: 'Ctrl+U', disabled: isUploading }
  ];

  return (
    <Box sx={{ 
      display: 'flex', 
      gap: 1, 
      p: 2, 
      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
      background: alpha(theme.palette.grey[50], 0.8)
    }}>
      {tools.map((tool, index) => (
        <Tooltip
          key={index}
          title={tool.tooltip}
          placement="top"
          arrow
        >
          <IconButton
          size="small"
          onClick={tool.action}
          disabled={tool.disabled}
          sx={{
            border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
            borderRadius: 2,
            backgroundColor: 'white',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                borderColor: theme.palette.primary.main
            },
            '&:disabled': {
              opacity: 0.5,
                backgroundColor: theme.palette.grey[100]
            },
              '&:active': {
                transform: 'scale(0.95)'
              },
              transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.1s ease'
          }}
        >
          {tool.icon}
        </IconButton>
        </Tooltip>
      ))}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </Box>
  );
};

const TaskEditor = ({ 
  task, 
  onSave, 
  onCancel, 
  users, 
  currentUser,
  mode = 'edit',
  timeFormat = '24hour'
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: null,
    start_date: null,
    column_type: 'today',
    tags: [],
    estimated_hours: ''
  });
  
  const [newTag, setNewTag] = useState('');
  const [descriptionRef, setDescriptionRef] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [editorFocused, setEditorFocused] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [commandMenuOpen, setCommandMenuOpen] = useState(false);
  const [commandMenuAnchor, setCommandMenuAnchor] = useState(null);
  const [filteredCommands, setFilteredCommands] = useState([]);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [previewMode, setPreviewMode] = useState(false);
  
  // Collaboration state
  const [collaborators, setCollaborators] = useState([]);
  const [loadingCollaborators, setLoadingCollaborators] = useState(false);

  useEffect(() => {
    if (task && mode === 'edit') {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        due_date: task.due_date ? dayjs(task.due_date) : null,
        start_date: task.start_date ? dayjs(task.start_date) : null,
        column_type: task.column_type || 'today',
        tags: task.tags || [],
        estimated_hours: task.estimated_hours || ''
      });
      
      // Load collaborators if task exists
      if (task.id) {
        loadCollaborators(task.id);
      }
    } else if (mode === 'create') {
              setFormData({
        title: '',
        description: '',
        priority: 'medium',
        due_date: null,
        start_date: null,
        column_type: task?.defaultColumn || 'today',
        tags: [],
        estimated_hours: ''
      });
      setCollaborators([]);
    }
  }, [task, mode]); // Removed currentUser dependency

  const commandSections = [
    {
      title: 'FORMAT',
      commands: [
        { name: '/heading1', alias: '/h1', icon: <HeaderIcon />, description: 'Big section heading', action: () => insertMarkdown('\n# ', '') },
        { name: '/heading2', alias: '/h2', icon: <HeaderIcon />, description: 'Medium section heading', action: () => insertMarkdown('\n## ', '') },
        { name: '/heading3', alias: '/h3', icon: <HeaderIcon />, description: 'Small section heading', action: () => insertMarkdown('\n### ', '') },
        { name: '/text', alias: '/p', icon: <AssignmentIcon />, description: 'Paragraph block', action: () => insertMarkdown('\n', '') },
        { name: '/quote', alias: '/q', icon: <QuoteIcon />, description: 'Add a blockquote section', action: () => insertMarkdown('\n> ', '') },
        { name: '/code', alias: '/c', icon: <CodeIcon />, description: 'Add a code section', action: () => insertMarkdown('\n```\n', '\n```\n') },
        { name: '/bold', alias: '/b', icon: <BoldIcon />, description: 'Bold text', action: () => insertMarkdown('**', '**') },
        { name: '/italic', alias: '/i', icon: <ItalicIcon />, description: 'Italic text', action: () => insertMarkdown('*', '*') }
      ]
    },
    {
      title: 'STRUCTURE',
      commands: [
        { name: '/separator', alias: '/hr', icon: <HRuleIcon />, description: 'Insert a horizontal rule separator', action: () => insertMarkdown('\n---\n', '') },
        { name: '/bullet', alias: '/ul', icon: <BulletIcon />, description: 'Create a simple bulleted list', action: () => insertMarkdown('\n- ', '') },
        { name: '/number', alias: '/ol', icon: <NumberedIcon />, description: 'Create a list with numbering', action: () => insertMarkdown('\n1. ', '') },
        { name: '/checklist', alias: '/check', icon: <ChecklistIcon />, description: 'Track tasks with a checklist', action: () => insertMarkdown('\n- [ ] ', '') },
        { name: '/2columns', alias: '/2col', icon: <ColumnsIcon />, description: 'Convert into 2 columns', action: () => insertMarkdown('\n| Column 1 | Column 2 |\n|----------|----------|\n| ', ' | |\n') },
        { name: '/3columns', alias: '/3col', icon: <ColumnsIcon />, description: 'Convert into 3 columns', action: () => insertMarkdown('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| ', ' | | |\n') },
        { name: '/table', alias: '/t', icon: <TableIcon />, description: 'Insert a table', action: () => insertMarkdown('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', '') },
        { name: '/toc', alias: '/contents', icon: <TOCIcon />, description: 'Table of Contents', action: () => generateTableOfContents() }
      ]
    },
    {
      title: 'BANNER',
      commands: [
        { name: '/info', alias: '/i', icon: <InfoIcon />, description: 'Insert an info banner', action: () => insertMarkdown('\n> ℹ️ **Info:** ', '\n') },
        { name: '/success', alias: '/s', icon: <SuccessIcon />, description: 'Insert a success banner', action: () => insertMarkdown('\n> ✅ **Success:** ', '\n') },
        { name: '/warning', alias: '/w', icon: <WarningIcon />, description: 'Insert a warning banner', action: () => insertMarkdown('\n> ⚠️ **Warning:** ', '\n') },
        { name: '/danger', alias: '/d', icon: <DangerIcon />, description: 'Insert a danger banner', action: () => insertMarkdown('\n> ❌ **Danger:** ', '\n') }
      ]
    },
    {
      title: 'NAVIGATION',
      commands: [
        { name: '/link', alias: '/l', icon: <LinkIcon />, description: 'Add a link', action: () => insertMarkdown('[', '](url)') }
      ]
    }
  ];

  // Flatten commands for filtering
  const commands = commandSections.flatMap(section => section.commands);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Handle slash command menu
    if (field === 'description') {
      const cursorPos = descriptionRef?.selectionStart || value.length;
      const beforeCursor = value.substring(0, cursorPos);
      const lines = beforeCursor.split('\n');
      const currentLine = lines[lines.length - 1];
      
      // Show menu when line starts with / (even just "/")
      if (currentLine.startsWith('/')) {
        const searchTerm = currentLine.substring(1).toLowerCase(); // Remove the /
        let filtered;
        
        if (searchTerm === '') {
          // Show all commands when just "/" is typed
          filtered = commands;
        } else {
          // Filter commands based on search term - more flexible matching
          filtered = commands.filter(cmd => {
            const nameMatch = cmd.name.substring(1).toLowerCase().includes(searchTerm);
            const aliasMatch = cmd.alias.substring(1).toLowerCase().includes(searchTerm);
            const descMatch = cmd.description.toLowerCase().includes(searchTerm);
            
            // Special keyword matching
            const keywordMatches = {
              'head': cmd.name.includes('heading'),
              'header': cmd.name.includes('heading'),
              'h': cmd.name.includes('heading'),
              'list': cmd.name.includes('bullet') || cmd.name.includes('number') || cmd.name.includes('checklist'),
              'table': cmd.name.includes('table') || cmd.name.includes('column'),
              'banner': cmd.name.includes('info') || cmd.name.includes('success') || cmd.name.includes('warning') || cmd.name.includes('danger'),
              'format': cmd.name.includes('bold') || cmd.name.includes('italic') || cmd.name.includes('code'),
              'text': cmd.name.includes('text') || cmd.name.includes('quote') || cmd.name.includes('bold') || cmd.name.includes('italic')
            };
            
            const keywordMatch = keywordMatches[searchTerm] || false;
            
            return nameMatch || aliasMatch || descMatch || keywordMatch;
          });
        }
        
        if (filtered.length > 0) {
          setFilteredCommands(filtered);
          setSelectedCommandIndex(0);
          setCommandMenuOpen(true);
          
          // Position menu at cursor line
          setTimeout(() => {
            if (descriptionRef) {
              // Get the textarea element
              const textarea = descriptionRef;
              const rect = textarea.getBoundingClientRect();
              
              // Create a canvas to measure text width accurately
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              const computedStyle = window.getComputedStyle(textarea);
              ctx.font = `${computedStyle.fontSize} ${computedStyle.fontFamily}`;
              
              // Get text before cursor and split into lines
              const textBeforeCursor = value.substring(0, cursorPos);
              const lines = textBeforeCursor.split('\n');
              const currentLineText = lines[lines.length - 1];
              const lineNumber = lines.length - 1;
              
              // Calculate line height
              const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.2;
              const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
              const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
              
              // Calculate cursor position
              const lineY = paddingTop + (lineNumber * lineHeight) - textarea.scrollTop;
              const lineX = paddingLeft + ctx.measureText(currentLineText).width;
              
              // Position menu
              setCommandMenuAnchor({
                getBoundingClientRect: () => ({
                  left: rect.left + lineX + 5,
                  top: rect.top + lineY + lineHeight,
                  right: rect.left + lineX + 5,
                  bottom: rect.top + lineY + lineHeight,
                  width: 0,
                  height: 0
                })
              });
            }
          }, 10);
        } else {
          setCommandMenuOpen(false);
        }
      } else {
        setCommandMenuOpen(false);
      }
    }
  };

  const handleSlashCommand = (text, cursorPos) => {
    const beforeCursor = text.substring(0, cursorPos);
    const afterCursor = text.substring(cursorPos);
    const lines = beforeCursor.split('\n');
    const currentLine = lines[lines.length - 1];
    
    // Check if current line starts with a slash command
    const commandMatch = currentLine.match(/^\/(\w+)$/);
    if (!commandMatch) return null;
    
    const command = commandMatch[1].toLowerCase();
    const lineStart = beforeCursor.lastIndexOf('\n') + 1;
    
    switch (command) {
      case 'bold':
      case 'b':
        return {
          newText: beforeCursor.substring(0, lineStart) + '****' + afterCursor,
          newCursorPos: lineStart + 2
        };
      case 'italic':
      case 'i':
        return {
          newText: beforeCursor.substring(0, lineStart) + '**' + afterCursor,
          newCursorPos: lineStart + 1
        };
      case 'code':
      case 'c':
        return {
          newText: beforeCursor.substring(0, lineStart) + '``' + afterCursor,
          newCursorPos: lineStart + 1
        };
      case 'link':
      case 'l':
        return {
          newText: beforeCursor.substring(0, lineStart) + '[](url)' + afterCursor,
          newCursorPos: lineStart + 1
        };
      case 'bullet':
      case 'ul':
        return {
          newText: beforeCursor.substring(0, lineStart) + '- ' + afterCursor,
          newCursorPos: lineStart + 2
        };
      case 'number':
      case 'ol':
        return {
          newText: beforeCursor.substring(0, lineStart) + '1. ' + afterCursor,
          newCursorPos: lineStart + 3
        };
      case 'header':
      case 'h':
        return {
          newText: beforeCursor.substring(0, lineStart) + '# ' + afterCursor,
          newCursorPos: lineStart + 2
        };
      case 'header2':
      case 'h2':
        return {
          newText: beforeCursor.substring(0, lineStart) + '## ' + afterCursor,
          newCursorPos: lineStart + 3
        };
      case 'header3':
      case 'h3':
        return {
          newText: beforeCursor.substring(0, lineStart) + '### ' + afterCursor,
          newCursorPos: lineStart + 4
        };
      case 'quote':
      case 'q':
        return {
          newText: beforeCursor.substring(0, lineStart) + '> ' + afterCursor,
          newCursorPos: lineStart + 2
        };
      case 'hr':
      case 'rule':
        return {
          newText: beforeCursor.substring(0, lineStart) + '---\n' + afterCursor,
          newCursorPos: lineStart + 4
        };
      case 'table':
      case 't':
        const tableText = '| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n';
        return {
          newText: beforeCursor.substring(0, lineStart) + tableText + afterCursor,
          newCursorPos: lineStart + 11
        };
      default:
        return null;
    }
  };

  const executeCommand = (command) => {
    const textarea = descriptionRef;
    const value = formData.description;
    const cursorPos = textarea?.selectionStart || value.length;
    const beforeCursor = value.substring(0, cursorPos);
    const afterCursor = value.substring(cursorPos);
    
    // Find the start of the current line
    const lineStart = beforeCursor.lastIndexOf('\n') + 1;
    const currentLine = beforeCursor.substring(lineStart);
    
    // Remove the entire slash command (including the /) from current line
    const slashIndex = currentLine.lastIndexOf('/');
    if (slashIndex !== -1) {
      const beforeSlash = beforeCursor.substring(0, lineStart + slashIndex);
      const cleanedText = beforeSlash + afterCursor;
      const newCursorPos = lineStart + slashIndex;
      
      setCommandMenuOpen(false);
      
      // Execute command directly with cleaned text
      setTimeout(() => {
        // Manually execute the markdown insertion with cleaned text
        const insertAt = newCursorPos;
        let finalText;
        
        if (command.name === '/bold') {
          finalText = cleanedText.substring(0, insertAt) + '****' + cleanedText.substring(insertAt);
          setFormData(prev => ({ ...prev, description: finalText }));
          setTimeout(() => {
            textarea?.setSelectionRange(insertAt + 2, insertAt + 2);
          }, 0);
        } else if (command.name === '/italic') {
          finalText = cleanedText.substring(0, insertAt) + '**' + cleanedText.substring(insertAt);
          setFormData(prev => ({ ...prev, description: finalText }));
          setTimeout(() => {
            textarea?.setSelectionRange(insertAt + 1, insertAt + 1);
          }, 0);
        } else {
          // For other commands, extract the before/after parts from the action
          const commandActions = {
            '/heading1': { before: '\n# ', after: '' },
            '/heading2': { before: '\n## ', after: '' },
            '/heading3': { before: '\n### ', after: '' },
            '/text': { before: '\n', after: '' },
            '/quote': { before: '\n> ', after: '' },
            '/code': { before: '\n```\n', after: '\n```\n' },
            '/separator': { before: '\n---\n', after: '' },
            '/bullet': { before: '\n- ', after: '' },
            '/number': { before: '\n1. ', after: '' },
            '/checklist': { before: '\n- [ ] ', after: '' },
            '/2columns': { before: '\n| Column 1 | Column 2 |\n|----------|----------|\n| ', after: ' | |\n' },
            '/3columns': { before: '\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| ', after: ' | | |\n' },
            '/table': { before: '\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', after: '' },
            '/info': { before: '\n> ℹ️ **Info:** ', after: '\n' },
            '/success': { before: '\n> ✅ **Success:** ', after: '\n' },
            '/warning': { before: '\n> ⚠️ **Warning:** ', after: '\n' },
            '/danger': { before: '\n> ❌ **Danger:** ', after: '\n' },
            '/link': { before: '[', after: '](url)' },
            '/toc': null // Special handling for TOC
          };
          
          const action = commandActions[command.name];
          if (action === null && command.name === '/toc') {
            // Special handling for Table of Contents - use the cleaned text
            setTimeout(() => {
              // Set the cleaned text first (without /toc)
              setFormData(prev => ({ ...prev, description: cleanedText }));
              
              // Then generate TOC with the cleaned text
              setTimeout(() => {
                if (textarea) {
                  textarea.focus();
                  textarea.setSelectionRange(insertAt, insertAt);
                }
                
                // Generate TOC using the cleaned text
                const currentText = cleanedText;
                const lines = currentText.split('\n');
                const headers = [];
                
                // Find all headers in the document
                lines.forEach((line, index) => {
                  const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
                  if (headerMatch) {
                    const level = headerMatch[1].length;
                    const title = headerMatch[2].trim();
                    const anchor = title.toLowerCase()
                      .replace(/[^\w\s-]/g, '')
                      .replace(/\s+/g, '-')
                      .replace(/-+/g, '-')
                      .replace(/^-|-$/g, '');
                    
                    headers.push({ level, title, anchor });
                  }
                });
                
                // Generate TOC markdown
                let tocMarkdown;
                
                if (headers.length === 0) {
                  tocMarkdown = `## Table of Contents

> Add headings to fill the Table of Contents

*Use # for main sections, ## for subsections, etc.*`;
                } else {
                  tocMarkdown = '## Table of Contents\n\n';
                  headers.forEach(header => {
                    const indent = '  '.repeat(Math.max(0, header.level - 1));
                    tocMarkdown += `${indent}- [${header.title}](#${header.anchor})\n`;
                  });
                }
                
                // Insert TOC at cursor position
                const beforeCursor = currentText.substring(0, insertAt);
                const afterCursor = currentText.substring(insertAt);
                const newText = beforeCursor + '\n' + tocMarkdown + '\n' + afterCursor;
                
                setFormData(prev => ({ ...prev, description: newText }));
                
                // Position cursor after the TOC
                setTimeout(() => {
                  const newCursorPos = insertAt + tocMarkdown.length + 2;
                  textarea?.setSelectionRange(newCursorPos, newCursorPos);
                }, 0);
              }, 10);
            }, 0);
          } else if (action) {
            finalText = cleanedText.substring(0, insertAt) + action.before + action.after + cleanedText.substring(insertAt);
            setFormData(prev => ({ ...prev, description: finalText }));
            setTimeout(() => {
              textarea?.setSelectionRange(insertAt + action.before.length, insertAt + action.before.length);
            }, 0);
          }
        }
      }, 0);
    }
  };

  const handleDescriptionKeyDown = (e) => {
    // Handle command menu navigation
    if (commandMenuOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedCommandIndex(prev => 
          prev < filteredCommands.length - 1 ? prev + 1 : 0
        );
        return;
      }
      
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedCommandIndex(prev => 
          prev > 0 ? prev - 1 : filteredCommands.length - 1
        );
        return;
      }
      
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (filteredCommands[selectedCommandIndex]) {
          executeCommand(filteredCommands[selectedCommandIndex]);
        }
        return;
      }
      
      if (e.key === 'Escape') {
        e.preventDefault();
        setCommandMenuOpen(false);
        return;
      }
    }

    // Handle keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          insertMarkdown('**', '**');
          return;
        case 'i':
          e.preventDefault();
          insertMarkdown('*', '*');
          return;
        case 'k':
          e.preventDefault();
          insertMarkdown('[', '](url)');
          return;
        case '`':
          e.preventDefault();
          insertMarkdown('`', '`');
          return;
        case 't':
          e.preventDefault();
          insertMarkdown('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', '');
          return;
        case 'h':
          e.preventDefault();
          insertMarkdown('\n---\n', '');
          return;
        case 'u':
          e.preventDefault();
          document.querySelector('input[type="file"]')?.click();
          return;
      }
      
      // Handle Ctrl+Shift combinations
      if (e.shiftKey) {
        switch (e.key) {
          case '*': // Ctrl+Shift+8
            e.preventDefault();
            insertMarkdown('\n- ', '');
            return;
          case '&': // Ctrl+Shift+7
            e.preventDefault();
            insertMarkdown('\n1. ', '');
            return;
        }
      }
    }

    // Handle Enter key for list continuation
    if (e.key === 'Enter' && !e.shiftKey) {
      const textarea = e.target;
      const value = textarea.value;
      const cursorPos = textarea.selectionStart;
      
      // Get the current line
      const lines = value.substring(0, cursorPos).split('\n');
      const currentLine = lines[lines.length - 1];
      
      // Check for bullet points
      const bulletMatch = currentLine.match(/^(\s*)-\s/);
      if (bulletMatch) {
        e.preventDefault();
        const indent = bulletMatch[1];
        const newText = value.substring(0, cursorPos) + '\n' + indent + '- ' + value.substring(cursorPos);
        setFormData(prev => ({ ...prev, description: newText }));
        
        setTimeout(() => {
          const newCursorPos = cursorPos + indent.length + 3;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
        return;
      }
      
      // Check for numbered lists
      const numberMatch = currentLine.match(/^(\s*)(\d+)\.\s/);
      if (numberMatch) {
        e.preventDefault();
        const indent = numberMatch[1];
        const nextNumber = parseInt(numberMatch[2]) + 1;
        const newText = value.substring(0, cursorPos) + '\n' + indent + nextNumber + '. ' + value.substring(cursorPos);
        setFormData(prev => ({ ...prev, description: newText }));
        
        setTimeout(() => {
          const newCursorPos = cursorPos + indent.length + nextNumber.toString().length + 3;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
        return;
      }
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert('Please enter a task title');
      return;
    }
    
    try {
      // Check for removed files when editing existing task
      if (mode === 'edit' && task?.description) {
        const originalFiles = extractFilesFromMarkdown(task.description);
        const currentFiles = extractFilesFromMarkdown(formData.description);
        
        // Find files that were removed
        const removedFiles = originalFiles.filter(filePath => !currentFiles.includes(filePath));
        
        // Delete removed files from storage
        for (const filePath of removedFiles) {
          await deleteFileFromStorage(filePath);
        }
      }
      
      const taskData = {
        title: formData.title.trim(),
        description: formData.description || '',
        priority: formData.priority || 'medium',
        assigned_to: currentUser?.id, // Always assign to current user
        due_date: formData.due_date ? formData.due_date.toISOString() : null,
        start_date: formData.start_date ? formData.start_date.toISOString() : null,
        column_type: formData.column_type || 'today',
        tags: formData.tags || [],
        estimated_hours: formData.estimated_hours || null,
        id: task?.id,
        created_by: mode === 'create' ? currentUser?.id : task?.created_by,
        status: task?.status || 'pending'
      };
      
      if (onSave) {
        await onSave(taskData);
        
        // Only save collaborators if this is an edit AND we are the task owner OR we have collaborators to save
        if (mode === 'edit' && task?.id && (task?.created_by === currentUser?.id || collaborators.length > 0)) {
          await saveCollaborators(task.id);
        } 
        
        setSnackbarMessage('Task saved successfully!');
        setSnackbarOpen(true);
      } else {
        console.error('onSave function not provided');
        alert('Save function not available');
      }
    } catch (error) {
      console.error('Error saving task:', error);
      setSnackbarMessage(`Failed to save task: ${error.message}`);
      setSnackbarOpen(true);
    }
  };

  // Collaboration functions
  const loadCollaborators = async (taskId) => {
    if (!taskId) return;
    
    setLoadingCollaborators(true);
    try {
      const { data: collaboratorsData, error } = await supabase
        .from('task_collaborators')
        .select('*')
        .eq('task_id', taskId);

      if (error) throw error;

      // Get user details for collaborators
      if (collaboratorsData && collaboratorsData.length > 0) {
        const userIds = collaboratorsData.map(c => c.user_id);
        
        const { data: usersData, error: usersError } = await supabase
          .from('profiles')
          .select('id, full_name, first_name, last_name')
          .in('id', userIds);

        if (usersError) throw usersError;

        // Get emails from auth.users
        const { data: emailsData, error: emailsError } = await supabase.rpc('get_user_emails_for_company', {
          p_company_id: users[0]?.company_id || null
        });

        if (emailsError) console.warn('Could not fetch emails:', emailsError);

        // Combine data
        const collaboratorsWithDetails = collaboratorsData.map(collab => {
          const userProfile = usersData?.find(u => u.id === collab.user_id);
          const userEmail = emailsData?.find(e => e.user_id === collab.user_id);
          return {
            ...collab,
            user: {
              id: collab.user_id,
              full_name: userProfile?.full_name || userProfile?.first_name || userProfile?.last_name,
              email: userEmail?.email
            }
          };
        });

        setCollaborators(collaboratorsWithDetails);
      } else {
        setCollaborators([]);
      }
    } catch (error) {
      console.error('Error loading collaborators:', error);
      setSnackbarMessage('Failed to load collaborators');
      setSnackbarOpen(true);
    } finally {
      setLoadingCollaborators(false);
    }
  };



  const handleRemoveCollaborator = async (collaboratorId, userId) => {
    if (!task?.id) return;

    try {
      const { error } = await supabase.rpc('remove_task_collaborator', {
        p_task_id: task.id,
        p_user_id: userId
      });

      if (error) throw error;

      setSnackbarMessage('Collaborator removed');
      setSnackbarOpen(true);
      
      // Reload collaborators
      await loadCollaborators(task.id);

    } catch (error) {
      console.error('Error removing collaborator:', error);
      setSnackbarMessage('Failed to remove collaborator');
      setSnackbarOpen(true);
    }
  };

  // Silent version for bulk removal operations
  const removeCollaboratorSilent = async (userId) => {
    if (!task?.id) return;

    try {
      const { error } = await supabase.rpc('remove_task_collaborator', {
        p_task_id: task.id,
        p_user_id: userId
      });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing collaborator:', error);
      return false;
    }
  };

  const saveCollaborators = async (taskId) => {
    try {
      
      // Only save if we actually have collaborators to save
      if (collaborators.length > 0) {
        // Get current user's company properly
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: companyData } = await supabase
          .from('companies')
          .select('id')
          .or(`created_by.eq.${user.id},allowed_users.cs.{${user.id}}`)
          .single();

        if (!companyData) {
          throw new Error('No company found for user');
        }

        // Use UPSERT instead of DELETE+INSERT
        const collaboratorData = collaborators.map(collaborator => {
          
          // The collaborator structure has user_id as the actual UUID
          const userId = collaborator.user_id || collaborator.id;
          
          // Validate that this user exists in our users list
          const userExists = users.find(u => u.id === userId);
          
          return {
            task_id: taskId,
            user_id: userId,
            company_id: companyData.id,
            invited_by: user.id,
            status: 'active'
          };
        });

        // Use upsert with on_conflict parameter
        const { data: insertResult, error: insertError } = await supabase
          .from('task_collaborators')
          .upsert(collaboratorData, {
            onConflict: 'task_id,user_id'
          });

        if (insertError) {
          console.error('Error upserting collaborators:', insertError);
          throw insertError;
        }
      } 
    } catch (error) {
      console.error('Error saving collaborators:', error);
      throw error;
    }
  };

  const insertMarkdown = (before, after) => {
    if (descriptionRef) {
      const start = descriptionRef.selectionStart || 0;
      const end = descriptionRef.selectionEnd || 0;
      const selectedText = formData.description.substring(start, end);
      const newText = formData.description.substring(0, start) + 
                     before + selectedText + after + 
                     formData.description.substring(end);
      
      setFormData(prev => ({ ...prev, description: newText }));
      
      setTimeout(() => {
        descriptionRef.focus();
        descriptionRef.setSelectionRange(
          start + before.length,
          start + before.length + selectedText.length
        );
      }, 0);
    } else {
      const newText = formData.description + before + after;
      setFormData(prev => ({ ...prev, description: newText }));
    }
  };

  const generateTableOfContents = () => {
    // Get current text
    const currentText = formData.description;
    const lines = currentText.split('\n');
    const headers = [];
    
    // Find all headers in the document
    lines.forEach((line, index) => {
      const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
      if (headerMatch) {
        const level = headerMatch[1].length;
        const title = headerMatch[2].trim();
        const anchor = title.toLowerCase()
          .replace(/[^\w\s-]/g, '') // Remove special chars
          .replace(/\s+/g, '-') // Replace spaces with hyphens
          .replace(/-+/g, '-') // Replace multiple hyphens with single
          .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
        
        headers.push({ level, title, anchor });
      }
    });
    
    // Generate TOC markdown
    let tocMarkdown;
    
    if (headers.length === 0) {
      // No headers found, insert a sample TOC
      tocMarkdown = `## Table of Contents

> Add headings to fill the Table of Contents

*Use # for main sections, ## for subsections, etc.*`;
    } else {
      // Generate actual TOC
      tocMarkdown = '## Table of Contents\n\n';
      
      headers.forEach(header => {
        const indent = '  '.repeat(Math.max(0, header.level - 1));
        tocMarkdown += `${indent}- [${header.title}](#${header.anchor})\n`;
      });
    }
    
    // Get cursor position and insert the TOC
    const textarea = descriptionRef;
    const cursorPos = textarea?.selectionStart || 0;
    const beforeCursor = currentText.substring(0, cursorPos);
    const afterCursor = currentText.substring(cursorPos);
    
    const newText = beforeCursor + '\n' + tocMarkdown + '\n' + afterCursor;
    setFormData(prev => ({ ...prev, description: newText }));
    
    // Position cursor after the TOC
    setTimeout(() => {
      const newCursorPos = cursorPos + tocMarkdown.length + 2;
      textarea?.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const userId = currentUser?.id;
      if (!userId) {
        alert('You must be logged in to upload files.');
        return;
      }
      const filePath = `${userId}/${fileName}`;

      const { data, error } = await supabase.storage
        .from('to-do-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Upload error:', error);
        alert(`Failed to upload image: ${error.message}`);
        return;
      }

      const imageMarkdown = `![${file.name}](https://todo-file/${filePath})`;
      insertMarkdown(imageMarkdown, '');
    } catch (error) {
      console.error('Upload catch error:', error);
      alert(`Failed to upload image: ${error.message || 'Unknown error'}`);
    } finally {
      setIsUploading(false);
    }
  };

  const deleteFileFromStorage = async (filePath) => {
    try {
      const { error } = await supabase.storage
        .from('to-do-files')
        .remove([filePath]);

      if (error) {
        console.error('Error deleting file from storage:', error);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const extractFilesFromMarkdown = (markdown) => {
    const fileRegex = /!\[.*?\]\(https:\/\/todo-file\/(.*?)\)/g;
    const files = [];
    let match;
    
    while ((match = fileRegex.exec(markdown)) !== null) {
      files.push(match[1]); // Extract the file path
    }
    
    return files;
  };

  const assignedUser = users?.find(u => u.id === formData.assigned_to) || currentUser;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Fade in timeout={800}>
        <Box sx={{ 
          minHeight: '100vh', 
          backgroundColor: alpha(theme.palette.grey[50], 0.5),
          pb: 4
        }}>
          <Container maxWidth="xl" sx={{ py: 4, px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100vw !important' }}>
            <Grid container spacing={2}>
              {/* Top Row - Title (Full Width) */}
              <Grid item xs={12}>
                {/* Title */}
                <SectionCard sx={{ borderRadius: 3, mb: 1 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ 
                          p: 1, 
                          borderRadius: 2, 
                          background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }}>
                          <AssignmentIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1.1rem' }}>
                          Task Title
                        </Typography>
                      </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Button
                            variant="outlined"
                            onClick={onCancel}
                            sx={{
                              borderRadius: 2,
                              fontWeight: 500,
                              px: 3,
                              py: 1.5,
                              fontSize: '0.95rem',
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.grey[100], 0.8),
                                transform: 'translateY(-1px)'
                              }
                            }}
                          >
                            <ArrowBackIcon sx={{ mr: 1, fontSize: 20 }} />
                            Back to Board
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleSave}
                            sx={{
                              borderRadius: 2,
                              fontWeight: 500,
                              px: 3,
                              py: 1.5,
                              fontSize: '0.95rem',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                              },
                              '&:disabled': {
                                opacity: 0.6,
                                transform: 'none',
                                boxShadow: 'none'
                              }
                            }}
                          >
                            <SaveIcon sx={{ mr: 1, fontSize: 20 }} />
                            Save Task
                          </Button>
                        </Box>
                    </Box>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Enter task title..."
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      InputProps={{
                        sx: {
                          fontSize: '1.1rem',
                          fontWeight: 500,
                          borderRadius: 3,
                          mt: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px',
                            borderColor: alpha(theme.palette.grey[400], 0.8)
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px',
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px',
                            borderColor: theme.palette.primary.main
                          }
                        }
                      }}
                    />
                  </CardContent>
                </SectionCard>
              </Grid>

              {/* Second Row - Task Details */}
              <Grid item xs={12} lg={6}>
                {/* Quick Info Card */}
                <SectionCard sx={{ borderRadius: 3, mb: 1, minHeight: '420px', height: '420px' }}>
                  <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'primary.main', fontSize: '1rem' }}>
                      Task Details
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <FlagIcon sx={{ mr: 1, color: priorityColors[formData.priority], fontSize: 18 }} />
                      <Chip
                        label={formData.priority}
                        size="small"
                        sx={{
                          backgroundColor: priorityColors[formData.priority],
                          color: 'white',
                          fontWeight: 500,
                          textTransform: 'capitalize',
                          borderRadius: 2,
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>

                    {/* Show task owner/creator */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PersonIcon sx={{ mr: 1, color: 'primary.main', fontSize: 18 }} />
                      <ProfileAvatar
                        userId={task?.created_by || currentUser?.id}
                        user={users?.find(u => u.id === (task?.created_by || currentUser?.id)) || currentUser}
                        size={24}
                        sx={{
                          mr: 1,
                          bgcolor: 'grey.400',
                          fontSize: '0.75rem',
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          color: 'white'
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                        {(() => {
                          const taskOwner = users?.find(u => u.id === (task?.created_by || currentUser?.id));
                          return `${taskOwner?.full_name || taskOwner?.email || 'Unknown'} (Owner)`;
                        })()}
                      </Typography>
                    </Box>

                    {formData.due_date && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ScheduleIcon sx={{ mr: 1, color: 'warning.main', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                          Due: {formData.due_date.format(timeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}
                        </Typography>
                      </Box>
                    )}

                    {formData.start_date && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ScheduleIcon sx={{ mr: 1, color: 'success.main', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                          Start: {formData.start_date.format(timeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AssignmentIcon sx={{ mr: 1, color: 'info.main', fontSize: 18 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                        Column: {formData.column_type === 'today' ? 'Today' : 
                                formData.column_type === 'this_week' ? 'This Week' :
                                formData.column_type === 'this_month' ? 'This Month' :
                                formData.column_type === 'later' ? 'Later' :
                                formData.column_type === 'completed' ? 'Completed' : 'Not set'}
                      </Typography>
                    </Box>

                    {formData.estimated_hours && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ScheduleIcon sx={{ mr: 1, color: 'warning.main', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                          Estimated: {formData.estimated_hours} hours
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AssignmentIcon sx={{ mr: 1, color: 'primary.main', fontSize: 18 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                        Status: {(task?.status || 'pending').charAt(0).toUpperCase() + (task?.status || 'pending').slice(1)}
                      </Typography>
                    </Box>

                    {/* Collaborators Section */}
                    {collaborators.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <GroupIcon sx={{ mr: 1, color: 'info.main', fontSize: 18 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                            Collaborators ({collaborators.length})
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: 3 }}>
                          {collaborators
                            .filter(collab => collab.user_id !== (task?.created_by || currentUser?.id)) // Exclude owner from collaborators list
                            .map((collab) => {
                              const isCurrentUser = collab.user_id === currentUser?.id;
                              return (
                                <Chip
                                  key={collab.id}
                                  label={`${collab.user?.full_name || collab.user?.email || 'Unknown'}`}
                                  size="small"
                                  avatar={
                                    <ProfileAvatar
                                      userId={collab.user_id}
                                      user={collab.user}
                                      size={20}
                                      sx={{ 
                                        width: '20px !important', 
                                        height: '20px !important', 
                                        backgroundColor: '#9e9e9e !important', 
                                        fontSize: '0.75rem !important', 
                                        color: '#ffffff !important', 
                                        border: '1px solid #ccc'
                                      }}
                                    />
                                  }
                                  sx={{ 
                                    borderRadius: 2, 
                                    fontSize: '0.75rem',
                                    height: 20
                                  }}
                                />
                              );
                            })}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </SectionCard>
              </Grid>

              {/* Second Row - Settings */}
              <Grid item xs={12} lg={6}>
                {/* Settings Card */}
                <SectionCard sx={{ borderRadius: 3, mb: 1, minHeight: '420px', height: '420px' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'primary.main', fontSize: '1rem' }}>
                      Settings
                    </Typography>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel sx={{ fontSize: '0.9rem' }}>Priority</InputLabel>
                      <Select
                        value={formData.priority}
                        label="Priority"
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                        size="small"
                        sx={{ 
                          borderRadius: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px'
                          }
                        }}
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="urgent">Urgent</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel sx={{ fontSize: '0.9rem' }}>Column</InputLabel>
                      <Select
                        value={formData.column_type}
                        label="Column"
                        onChange={(e) => handleInputChange('column_type', e.target.value)}
                        size="small"
                        sx={{ 
                          borderRadius: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px'
                          }
                        }}
                      >
                        <MenuItem value="today">Today</MenuItem>
                        <MenuItem value="this_week">This Week</MenuItem>
                        <MenuItem value="this_month">This Month</MenuItem>
                        <MenuItem value="later">Later</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel sx={{ fontSize: '0.9rem' }}>Assigned To</InputLabel>
                      <Select
                        multiple
                        disabled={task?.created_by && task.created_by !== currentUser?.id} // Disable if not the owner
                        value={(() => {
                          // Always include the task owner/creator
                          const taskOwner = task?.created_by || currentUser?.id;
                          const collaboratorIds = collaborators.map(c => c.user_id);
                          
                          // Combine owner + collaborators, removing duplicates
                          const allAssigned = [taskOwner, ...collaboratorIds];
                          return [...new Set(allAssigned)].filter(Boolean);
                        })()}
                        label="Assigned To"
                        onChange={async (e) => {
                          const selectedIds = e.target.value;
                          const taskOwner = task?.created_by || currentUser?.id;
                          
                          // Ensure task owner is always included
                          if (!selectedIds.includes(taskOwner)) {
                            selectedIds.push(taskOwner);
                          }
                          
                          // Find collaborators that were removed (only for existing tasks)
                          if (mode === 'edit' && task?.id) {
                            const currentCollaboratorIds = collaborators.map(c => c.user_id);
                            const removedCollaboratorIds = currentCollaboratorIds.filter(id => 
                              !selectedIds.includes(id) && id !== taskOwner
                            );
                            
                            // Remove collaborators from database silently
                            for (const removedUserId of removedCollaboratorIds) {
                              await removeCollaboratorSilent(removedUserId);
                            }
                          }
                          
                          // Create collaborators array (excluding the task owner)
                          const newCollaborators = selectedIds
                            .filter(id => id !== taskOwner)
                            .map(userId => {
                              const user = users.find(u => u.id === userId);
                              return {
                                id: userId, // Use actual user ID, not temp ID
                                user_id: userId,
                                user: {
                                  id: userId,
                                  full_name: user?.full_name,
                                  email: user?.email
                                }
                              };
                            });
                          setCollaborators(newCollaborators);
                        }}
                        size="small"
                        sx={{ 
                          borderRadius: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px'
                          }
                        }}
                        renderValue={(selected) => {
                          const taskOwner = task?.created_by || currentUser?.id;
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((userId) => {
                                const user = users.find(u => u.id === userId);
                                const isOwner = userId === taskOwner;
                                const isCurrentUser = userId === currentUser?.id;
                                
                                let label = user?.full_name || user?.email || 'Unknown';
                                if (isCurrentUser) {
                                  label += ' (You)';
                                } else if (isOwner) {
                                  label += ' (Owner)';
                                }
                                
                                return (
                                  <Chip
                                    key={userId}
                                    label={label}
                                    size="small"
                                    avatar={
                                      <ProfileAvatar
                                        userId={userId}
                                        user={user}
                                        size={20}
                                        sx={{ 
                                          width: '20px !important', 
                                          height: '20px !important', 
                                          backgroundColor: '#9e9e9e !important', 
                                          fontSize: '0.75rem !important', 
                                          color: '#ffffff !important', 
                                          border: '1px solid #ccc'
                                        }}
                                      />
                                    }
                                    sx={{ 
                                      borderRadius: 2, 
                                      fontSize: '0.8rem'
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          );
                        }}
                      >
                        {users.map((user) => {
                          const taskOwner = task?.created_by || currentUser?.id;
                          const isOwner = user.id === taskOwner;
                          const isCurrentUser = user.id === currentUser?.id;
                          
                          return (
                            <MenuItem 
                              key={user.id} 
                              value={user.id}
                              disabled={isOwner && !isCurrentUser} // Can't remove owner unless you are the owner
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ProfileAvatar
                                  userId={user.id}
                                  user={user}
                                  size={20}
                                  sx={{ bgcolor: 'grey.400', fontSize: '0.75rem', color: 'white' }}
                                />
                                <Typography sx={{ fontSize: '0.9rem' }}>
                                  {user.full_name || user.email}
                                  {isOwner && ' (Owner)'}
                                  {isCurrentUser && !isOwner && ' (You)'}
                                </Typography>
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Estimated Hours"
                      type="number"
                      value={formData.estimated_hours}
                      onChange={(e) => handleInputChange('estimated_hours', e.target.value)}
                      size="small"
                      sx={{ 
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px'
                        }
                      }}
                    />



                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2}}>
                      <DateTimePicker
                        label="Start Date"
                        value={formData.start_date}
                        onChange={(date) => handleInputChange('start_date', date)}
                        format={timeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                        ampm={timeFormat !== '24hour'}
                        renderInput={(params) => <TextField {...params} fullWidth size="small" sx={{ 
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          },
                          '& fieldset': {
                            borderWidth: '1px !important'
                          }
                        }} />}
                      />
                      
                      <DateTimePicker
                        label="Due Date"
                        value={formData.due_date}
                        onChange={(date) => handleInputChange('due_date', date)}
                        format={timeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                        ampm={timeFormat !== '24hour'}
                        renderInput={(params) => <TextField {...params} fullWidth size="small" sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          },
                          '& fieldset': {
                            borderWidth: '1px !important'
                          }
                        }} />}
                      />
                    </Box>
                  </CardContent>
                </SectionCard>
              </Grid>



              {/* Fourth Row - Full Width Description */}
              <Grid item xs={12}>
                {/* Description */}
                <SectionCard sx={{ borderRadius: 3, minHeight: '75vh' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    p: 2, 
                    pb: 0,
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem' }}>
                      Description
                    </Typography>
                    <IconButton
                      onClick={() => setPreviewMode(!previewMode)}
                      sx={{
                        border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                        borderRadius: 2,
                        backgroundColor: 'white',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          borderColor: theme.palette.primary.main
                        }
                      }}
                    >
                      {previewMode ? <EditIcon /> : <PreviewIcon />}
                    </IconButton>
                  </Box>

                    {!previewMode && (
                      <MarkdownToolbar onInsert={insertMarkdown} onImageUpload={handleImageUpload} isUploading={isUploading} />
                    )}
                    {previewMode && (
                      <Box sx={{ height: '73px' }} /> // Reserve space for toolbar when hidden
                    )}

                  <CardContent sx={{ p: 0, height: 'calc(75vh - 100px)', display: 'flex', flexDirection: 'column' }}>
                    {/* Live Markdown Editor */}
                      <Box sx={{ 
                      position: 'relative', 
                        height: '100%',
                      display: 'flex',
                        border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                        borderRadius: 3,
                      overflow: 'hidden'
                    }}>
                      {/* Left Side - Markdown Input */}
                      <Box sx={{ 
                        width: previewMode ? '0%' : (formData.description ? '50%' : '100%'), // Full width when no content
                        height: '100%',
                        transition: 'width 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        opacity: previewMode ? 0 : 1
                      }}>
                      <TextField
                        fullWidth
                        multiline
                        disabled={isUploading}
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                          onKeyDown={handleDescriptionKeyDown}
                          onFocus={() => {
                            setEditorFocused(true);
                            setCursorPosition(descriptionRef?.selectionStart || 0);
                          }}
                          onBlur={() => setEditorFocused(false)}
                          onClick={() => setCursorPosition(descriptionRef?.selectionStart || 0)}
                          onKeyUp={() => setCursorPosition(descriptionRef?.selectionStart || 0)}
                          placeholder=""
                        inputRef={setDescriptionRef}
                        sx={{
                          height: '100%',
                          '& .MuiInputBase-root': {
                            height: '100%',
                              alignItems: 'stretch',
                              borderRadius: 0
                          },
                          '& .MuiInputBase-inputMultiline': {
                            height: '100% !important',
                            resize: 'none',
                            overflow: 'auto !important'
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none'
                          }
                        }}
                        InputProps={{
                          sx: {
                            fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                            fontSize: '0.9rem',
                            lineHeight: 1.6,
                            height: '100%',
                              padding: 2,
                              borderRadius: 0
                            }
                          }}
                        />
                        
                        {/* Slash Command Hint Overlay */}
                        {(() => {
                          if (!descriptionRef || isUploading || !document.activeElement || document.activeElement !== descriptionRef) {
                            return null;
                          }
                          
                          const cursorPos = cursorPosition;
                          const beforeCursor = formData.description.substring(0, cursorPos);
                          const lines = beforeCursor.split('\n');
                          const currentLine = lines[lines.length - 1];
                          const lineNumber = lines.length - 1;
                          
                          // Show hint only on empty lines
                          if (currentLine.trim() !== '') {
                            return null;
                          }
                          
                          // Calculate cursor position more accurately
                          const computedStyle = window.getComputedStyle(descriptionRef);
                          const lineHeight = parseFloat(computedStyle.lineHeight) || 24;
                          const paddingTop = parseFloat(computedStyle.paddingTop) || 16;
                          const paddingLeft = parseFloat(computedStyle.paddingLeft) || 16;
                          
                          // Position at cursor line with proper offset
                          const lineY = paddingTop + (lineNumber * lineHeight) - (descriptionRef.scrollTop || 0) + 2;
                          const lineX = paddingLeft + 2;
                          
                          return (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: `${lineY}px`,
                                left: `${lineX}px`,
                                pointerEvents: 'none',
                                color: alpha(theme.palette.text.secondary, 0.4),
                                fontSize: '0.9rem',
                                fontFamily: computedStyle.fontFamily,
                                lineHeight: `${lineHeight}px`,
                                zIndex: 1,
                                userSelect: 'none',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Type '/' for commands
                            </Box>
                          );
                        })()}
                      </Box>
                      
                      {/* Right Side - Live Preview */}
                      {(formData.description || previewMode) && (
                        <Box sx={{ 
                          width: previewMode ? '100%' : '50%', // Full width in preview mode
                          height: '100%',
                          borderLeft: previewMode ? 'none' : `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                          overflow: 'auto',
                          backgroundColor: alpha(theme.palette.grey[50], 0.3),
                          p: 2,
                          transition: 'width 0.3s ease, border-left 0.3s ease'
                        }}>
                          <ReactMarkdown 
                            remarkPlugins={[remarkGfm]}
                            components={{
                              img: (props) => {
                                return <TodoImage {...props} currentUser={currentUser} />;
                              },
                              h1: ({ children }) => (
                                <h1 style={{
                                  fontSize: '1.5rem',
                                  fontWeight: 500,
                                  margin: '24px 0 16px 0',
                                  color: theme.palette.text.primary,
                                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                                  paddingBottom: '8px'
                                }}>
                                  {children}
                                </h1>
                              ),
                              h2: ({ children }) => (
                                <h2 style={{
                                  fontSize: '1.25rem',
                                  fontWeight: 500,
                                  margin: '20px 0 12px 0',
                                  color: theme.palette.text.primary,
                                  borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                                  paddingBottom: '6px'
                                }}>
                                  {children}
                                </h2>
                              ),
                              h3: ({ children }) => (
                                <h3 style={{
                                  fontSize: '1.1rem',
                                  fontWeight: 500,
                                  margin: '16px 0 8px 0',
                                  color: theme.palette.text.primary
                                }}>
                                  {children}
                                </h3>
                              ),
                              p: ({ children }) => (
                                <p style={{
                                  margin: '8px 0',
                                  lineHeight: '1.6',
                                  color: theme.palette.text.primary
                                }}>
                                  {children}
                                </p>
                              ),
                              ul: ({ children }) => (
                                <ul style={{
                                  margin: '8px 0',
                                  paddingLeft: '20px',
                                  color: theme.palette.text.primary
                                }}>
                                  {children}
                                </ul>
                              ),
                              ol: ({ children }) => (
                                <ol style={{
                                  margin: '8px 0',
                                  paddingLeft: '20px',
                                  color: theme.palette.text.primary
                                }}>
                                  {children}
                                </ol>
                              ),
                              li: ({ children, ...props }) => {
                                // Check if this is a task list item
                                const childrenArray = React.Children.toArray(children);
                                const firstChild = childrenArray[0];
                                
                                if (typeof firstChild === 'string') {
                                  // Handle checkbox lists
                                  if (firstChild.startsWith('☐ ')) {
                                    const taskText = firstChild.substring(2);
                                    const handleCheckboxChange = () => {
                                      const currentText = formData.description;
                                      const newText = currentText.replace(`- [ ] ${taskText}`, `- [x] ${taskText}`);
                                      handleInputChange('description', newText);
                                    };
                                    
                                    return (
                                      <li style={{
                                        margin: '4px 0',
                                        lineHeight: '1.5',
                                        color: theme.palette.text.primary,
                                        listStyle: 'none',
                                        display: 'flex',
                                        alignItems: 'flex-start'
                                      }}>
                                        <Box
                                          onClick={handleCheckboxChange}
                                          sx={{
                                            width: '18px',
                                            height: '18px',
                                            border: '2px solid',
                                            borderColor: theme.palette.grey[400],
                                            borderRadius: '4px',
                                            backgroundColor: 'white',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: '8px',
                                            marginTop: '2px',
                                            '&:hover': {
                                              borderColor: theme.palette.primary.main,
                                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                              transform: 'scale(1.1)'
                                            },
                                            transition: 'all 0.2s ease'
                                          }}
                                        />
                                        <span>{taskText}{childrenArray.slice(1)}</span>
                                      </li>
                                    );
                                  }
                                  if (firstChild.startsWith('☑ ')) {
                                    const taskText = firstChild.substring(2);
                                    const handleCheckboxChange = () => {
                                      const currentText = formData.description;
                                      const newText = currentText.replace(`- [x] ${taskText}`, `- [ ] ${taskText}`);
                                      handleInputChange('description', newText);
                                    };
                                    
                                    return (
                                      <li style={{
                                        margin: '4px 0',
                                        lineHeight: '1.5',
                                        color: theme.palette.text.primary,
                                        listStyle: 'none',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        textDecoration: 'line-through',
                                        opacity: 0.7
                                      }}>
                                        <Box
                                          onClick={handleCheckboxChange}
                                          sx={{
                                            width: '18px',
                                            height: '18px',
                                            border: '2px solid',
                                            borderColor: theme.palette.success.main,
                                            borderRadius: '4px',
                                            backgroundColor: theme.palette.success.main,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: '8px',
                                            marginTop: '2px',
                                            '&:hover': {
                                              backgroundColor: theme.palette.success.dark,
                                              transform: 'scale(1.1)'
                                            },
                                            transition: 'all 0.2s ease'
                                          }}
                                        >
                                          <Typography sx={{ 
                                            color: 'white', 
                                            fontSize: '14px', 
                                            lineHeight: 1,
                                            fontWeight: 700
                                          }}>
                                            ✓
                                          </Typography>
                                        </Box>
                                        <span>{taskText}{childrenArray.slice(1)}</span>
                                      </li>
                                    );
                                  }
                                }
                                
                                return (
                                  <li style={{
                                    margin: '4px 0',
                                    lineHeight: '1.5',
                                    color: theme.palette.text.primary
                                  }}>
                                    {children}
                                  </li>
                                );
                              },
                              blockquote: ({ children }) => (
                                <blockquote style={{
                                  margin: '16px 0',
                                  padding: '12px 16px',
                                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                                  backgroundColor: alpha(theme.palette.grey[100], 0.5),
                                  borderRadius: '0 4px 4px 0',
                                  fontStyle: 'italic',
                                  color: theme.palette.text.secondary
                                }}>
                                  {children}
                                </blockquote>
                              ),
                              table: ({ children }) => (
                                <table style={{
                                  width: '100%',
                                  borderCollapse: 'collapse',
                                  margin: '16px 0',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`
                                }}>
                                  {children}
                                </table>
                              ),
                              thead: ({ children }) => (
                                <thead style={{
                                  backgroundColor: alpha(theme.palette.grey[100], 0.8)
                                }}>
                                  {children}
                                </thead>
                              ),
                              th: ({ children }) => (
                                <th style={{
                                  padding: '12px',
                                  textAlign: 'left',
                                  fontWeight: 500,
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                  backgroundColor: alpha(theme.palette.grey[100], 0.8),
                                  color: theme.palette.text.primary
                                }}>
                                  {children}
                                </th>
                              ),
                              td: ({ children }) => (
                                <td style={{
                                  padding: '12px',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                  color: theme.palette.text.primary
                                }}>
                                  {children}
                                </td>
                              ),
                              hr: () => (
                                <hr style={{
                                  margin: '24px 0',
                                  border: 'none',
                                  height: '2px',
                                  backgroundColor: alpha(theme.palette.grey[400], 0.3),
                                  borderRadius: '1px'
                                }} />
                              ),
                              strong: ({ children }) => (
                                <strong style={{
                                  fontWeight: 700,
                                  color: theme.palette.text.primary
                                }}>
                                  {children}
                                </strong>
                              ),
                              em: ({ children }) => (
                                <em style={{
                                  fontStyle: 'italic',
                                  color: theme.palette.text.secondary
                                }}>
                                  {children}
                                </em>
                              ),
                              code: ({ inline, className, children, ...props }) => {
                                if (inline) {
                                  return (
                                    <code
                                      style={{
                                        backgroundColor: alpha(theme.palette.grey[100], 0.8),
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                                        fontSize: '0.85em',
                                        color: theme.palette.text.primary
                                      }}
                                      {...props}
                                    >
                                      {children}
                                    </code>
                                  );
                                }
                                return (
                                  <pre
                                    style={{
                                      backgroundColor: alpha(theme.palette.grey[900], 0.05),
                                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                      borderRadius: '8px',
                                      padding: '16px',
                                      margin: '16px 0',
                                      overflow: 'auto',
                                      fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                                      fontSize: '0.9em',
                                      lineHeight: '1.6',
                                      color: theme.palette.text.primary,
                                      whiteSpace: 'pre',
                                      tabSize: 4
                                    }}
                                  >
                                    <code {...props}>{children}</code>
                                  </pre>
                                );
                              }
                            }}
                          >
                            {(() => {
                              let content = formData.description || '*Type to see live preview...*';
                              // Pre-process to fix checkbox syntax
                              content = content.replace(/- \[ \]/g, '- ☐');
                              content = content.replace(/- \[x\]/g, '- ☑');
                              return content;
                            })()}
                          </ReactMarkdown>
                        </Box>
                      )}
                    </Box>
                      
                      {/* Command Menu */}
                    {commandMenuOpen && (
                      <Popper
                        open={commandMenuOpen}
                        anchorEl={commandMenuAnchor}
                        placement="bottom-start"
                        sx={{ zIndex: 9999 }}
                      >
                        <ClickAwayListener onClickAway={() => setCommandMenuOpen(false)}>
                          <Card sx={{ 
                            mt: 1, 
                            maxWidth: 300,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`
                          }}>
                            <Box sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
                              {(() => {
                                // Use the same logic as handleInputChange
                                const cursorPos = descriptionRef?.selectionStart || formData.description.length;
                                const beforeCursor = formData.description.substring(0, cursorPos);
                                const lines = beforeCursor.split('\n');
                                const currentLine = lines[lines.length - 1];
                                const searchTerm = currentLine.startsWith('/') ? currentLine.substring(1).toLowerCase() : '';
                                let currentIndex = 0;
                                
                                return commandSections.map((section) => {
                                  const sectionCommands = section.commands.filter(cmd => {
                                    if (searchTerm === '') return true;
                                    
                                    const nameMatch = cmd.name.substring(1).toLowerCase().includes(searchTerm);
                                    const aliasMatch = cmd.alias.substring(1).toLowerCase().includes(searchTerm);
                                    const descMatch = cmd.description.toLowerCase().includes(searchTerm);
                                    
                                    // Special keyword matching
                                    const keywordMatches = {
                                      'head': cmd.name.includes('heading'),
                                      'header': cmd.name.includes('heading'),
                                      'h': cmd.name.includes('heading'),
                                      'list': cmd.name.includes('bullet') || cmd.name.includes('number') || cmd.name.includes('checklist'),
                                      'table': cmd.name.includes('table') || cmd.name.includes('column'),
                                      'banner': cmd.name.includes('info') || cmd.name.includes('success') || cmd.name.includes('warning') || cmd.name.includes('danger'),
                                      'format': cmd.name.includes('bold') || cmd.name.includes('italic') || cmd.name.includes('code'),
                                      'text': cmd.name.includes('text') || cmd.name.includes('quote') || cmd.name.includes('bold') || cmd.name.includes('italic')
                                    };
                                    
                                    const keywordMatch = keywordMatches[searchTerm] || false;
                                    
                                    return nameMatch || aliasMatch || descMatch || keywordMatch;
                                  });
                                  
                                  if (sectionCommands.length === 0) return null;
                                  
                                  const sectionItems = [];
                                  
                                  // Section header
                                  sectionItems.push(
                                    <Box key={section.title} sx={{ 
                                      px: 2, 
                                      py: 1, 
                                      backgroundColor: alpha(theme.palette.grey[100], 0.5),
                                      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                                    }}>
                                      <Typography variant="caption" sx={{ 
                                        fontWeight: 600, 
                                        color: theme.palette.primary.main,
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px'
                                      }}>
                                        {section.title}
                                      </Typography>
                                    </Box>
                                  );
                                  
                                  // Section commands
                                  sectionCommands.forEach((command) => {
                                    const isSelected = currentIndex === selectedCommandIndex;
                                    sectionItems.push(
                                      <ListItem
                                        key={command.name}
                                        button
                                        selected={isSelected}
                                        onClick={() => executeCommand(command)}
                                        sx={{
                                          py: 1.5,
                                          px: 2,
                                          backgroundColor: isSelected 
                                            ? alpha(theme.palette.primary.main, 0.1) 
                                            : 'transparent',
                                          '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                          }
                                        }}
                                      >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                          <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: 1,
                                            backgroundColor: alpha(theme.palette.grey[100], 0.8),
                                            color: theme.palette.text.secondary
                                          }}>
                                            {command.icon}
                                          </Box>
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={
                                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                              {command.description.split(' ')[0]} {command.description.split(' ')[1] || ''}
                                            </Typography>
                                          }
                                          secondary={
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                              {command.description}
                                            </Typography>
                                          }
                                        />
                                      </ListItem>
                                    );
                                    currentIndex++;
                                  });
                                  
                                  return sectionItems;
                                }).filter(Boolean).flat();
                              })()}
                                                         </Box>
                           </Card>
                         </ClickAwayListener>
                       </Popper>
                    )}
                  </CardContent>
                  
                  {/* Save Button at bottom right */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, pt: 0, mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        px: 3,
                        py: 1.5,
                        fontSize: '0.95rem',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                        },
                        '&:disabled': {
                          opacity: 0.6,
                          transform: 'none',
                          boxShadow: 'none'
                        }
                      }}
                    >
                      <SaveIcon sx={{ mr: 1, fontSize: 20 }} />
                      Save Task
                    </Button>
                  </Box>
                </SectionCard>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Fade>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{
            width: '100%',
            borderRadius: 2,
            fontWeight: 500
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default TaskEditor;